import { joiResolver } from '@hookform/resolvers/joi';
import { DialogActions, DialogContent, Grid, Typography } from '@mui/material';
import Joi from 'joi';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { API } from '../../../global/config/api';
import { getFilters } from '../../../global/helpers/filters';
import { useServerCall } from '../../../global/helpers/hooks';
import CodelistStore from '../../../global/stores/CodelistStore';
import {
  XsAutocompleteServer,
  XsBaseDialog,
  XsButton,
  XsDropzone,
  XsIconButton,
  XsNumberField,
  XsSearchSelect,
  XsTableView,
  XsTextField
} from '../../../global/ui';
import { IAttachment, TColumnExt } from '../../../global/types/types';
import { IDeviceRow } from '../../../swagger/gd/codelist';
import DeviceDialogStore, {
  DEVICES_WITH_CAPACITY,
  DEVICES_WITH_CATASTRAL,
  IDeviceDialogForm,
  IInstalledDeviceEntry,
  uuidv4
} from './DeviceDialogStore';
import InstalledDeviceSelectDialog, { IInstalledDeviceSelection } from './InstalledDeviceSelectDialog';
import RequestFormStore from './RequestFormStore';

// ─── Joi schema ───────────────────────────────────────────────────────────────
// Podmienené required polia riešime cez ploché `deviceCode` (device.code_ext),
// NIE cez Joi.when('device', { is: Joi.any().custom(...) }) — custom rule, ktorá
// vráti boolean, totiž validáciu nikdy nezhodí, takže `is` by vždy prešla a
// kapacita/katastrálne polia by ostali natrvalo required (a Save by ticho padal).

const deviceDialogSchema = Joi.object<IDeviceDialogForm>({
  cityId: Joi.string().required(),
  street: Joi.string().required(),
  // device is an IDeviceRow object — validated as any (non-null check via custom).
  // V reimbursement toku sa sekcia "Zariadenie" vôbec nezobrazuje (nahrádza ju
  // InstalledDeviceSelectDialog), preto tam ostáva null a nevalidujeme ho.
  device: Joi.any().custom((v, h) => {
    if (RequestFormStore.requestType === 'reimbursement') return v;
    return v && typeof v === 'object' && v._id ? v : h.error('any.required');
  }),
  // Plochý kód zariadenia — synchronizovaný z `device` cez postAction, riadi podmienky nižšie
  deviceCode: Joi.string().allow('').optional(),
  devicePower: Joi.any().custom((v, h) => {
    if (RequestFormStore.requestType === 'reimbursement') return v;
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? v : h.error('any.invalid');
  }),
  // Capacity — required when deviceCode is in DEVICES_WITH_CAPACITY
  deviceCapacity: Joi.when('deviceCode', {
    is: Joi.string().valid(...DEVICES_WITH_CAPACITY),
    then: Joi.number().positive().required(),
    otherwise: Joi.alternatives().try(Joi.number(), Joi.string().allow('')).optional()
  }),
  // Cadastral area — required when deviceCode is in DEVICES_WITH_CATASTRAL (reservation flow),
  // alebo vždy pri reimbursement toku (pole je tam zobrazené priamo pod Obec/Ulica)
  deviceCadastralArea: Joi.when('deviceCode', {
    is: Joi.string().valid(...DEVICES_WITH_CATASTRAL),
    then: Joi.string().required(),
    otherwise: Joi.any().custom((v, h) => {
      if (RequestFormStore.requestType === 'reimbursement') {
        return v && String(v).trim() !== '' ? v : h.error('any.required');
      }
      return v;
    })
  }),
  // Parcel number — required same condition as cadastral
  deviceParcelNumber: Joi.when('deviceCode', {
    is: Joi.string().valid(...DEVICES_WITH_CATASTRAL),
    then: Joi.string().required(),
    otherwise: Joi.any().custom((v, h) => {
      if (RequestFormStore.requestType === 'reimbursement') {
        return v && String(v).trim() !== '' ? v : h.error('any.required');
      }
      return v;
    })
  }),
  // Count — required only in the "already installed" (reimbursement) flow; read live from
  // RequestFormStore (same reasoning as the deviceCode comment above — no static `is` here)
  deviceCount: Joi.any().custom((v, h) => {
    if (RequestFormStore.requestType !== 'reimbursement') return v;
    const n = Number(v);
    return Number.isInteger(n) && n >= 1 ? v : h.error('any.invalid');
  }),
  // Prílohy k nainštalovanému zariadeniu — voliteľné, len pri reimbursement toku
  deviceInstallationPhotos: Joi.array().optional(),
  deviceConnectionContract: Joi.array().optional(),
  deviceOwnerConsent: Joi.array().optional(),
  deviceLeaseContract: Joi.array().optional(),
  deviceLabelPhotos: Joi.array().optional()
}).options({ allowUnknown: false });

// Zoznam vybraných nainštalovaných zariadení — plnený z InstalledDeviceSelectDialog
// (handleInstalledDeviceConfirm); okrem zariadení s Druhom "Menič napätia" (VC), tie idú do tabuľky meničov nižšie
export interface IMockInstalledDeviceRow {
  type: string;
  specification: string;
  manufacturer: string;
  model: string;
  count: number;
  // Skryté polia (nezobrazujú sa v tabuľke) — pre zostavenie installed_devices_data pri uložení
  energeticDeviceId: string;
  attachments: IAttachment[];
  // Skryté polia — kódy/id zvolených Druh/Špecifikácia/Výrobca, pre predvyplnenie InstalledDeviceSelectDialog pri editácii riadku
  typeCode: string;
  specificationId: string;
  manufacturerCode: string;
}

// Zoznam vybraných meničov (invertorov) — plnený z InstalledDeviceSelectDialog, len ak je zvolený
// Druh zariadenia "Menič napätia" (code_ext 'VC')
export interface IMockInverterRow {
  manufacturer: string;
  model: string;
  count: number;
  energeticDeviceId: string;
  attachments: IAttachment[];
  // Skryté pole — pre zobrazenie v tabuľke miest (RequestForm), kde sa kategórie miešajú dokopy
  deviceTypeName: string;
  typeCode: string;
  specificationId: string;
  manufacturerCode: string;
}

// Zoznam vybraných batériových úložísk — plnený z InstalledDeviceSelectDialog, len ak je zvolený
// Druh zariadenia "Akumulátor" (code_ext 'BA')
export interface IMockBatteryStorageRow {
  manufacturer: string;
  model: string;
  count: number;
  energeticDeviceId: string;
  attachments: IAttachment[];
  deviceTypeName: string;
  typeCode: string;
  specificationId: string;
  manufacturerCode: string;
}

// Zoznam vybraných akumulačných nádrží (heat) — plnený z InstalledDeviceSelectDialog, len ak je
// zvolený Druh zariadenia "Akumulačná nádrž" (code_ext 'TS')
export interface IMockBatteryTankRow {
  manufacturer: string;
  model: string;
  count: number;
  energeticDeviceId: string;
  attachments: IAttachment[];
  deviceTypeName: string;
  typeCode: string;
  specificationId: string;
  manufacturerCode: string;
}

// Identifikátor jednej zo štyroch kategorizovaných mock tabuliek nižšie — používaný pri edit/delete akciách riadku
type TRowTable = 'installed' | 'inverter' | 'batteryStorage' | 'batteryTank';

// ─── Component ────────────────────────────────────────────────────────────────

function DeviceDialog() {
  const { t } = useTranslation();
  // Load device codelist into CodelistStore on mount (fires once per dialog open
  // because query key is static; data is cached by react-query)
  useServerCall<undefined, { rows: IDeviceRow[] }>({
    key: 'deviceCodelist',
    mode: 'auto',
    request: { method: 'GET', url: API.deviceCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => {
      CodelistStore.setCodelist('device', res.data?.rows ?? []);
    }
  });

  // Zariadenia ponúkané v dropdowne — filtrované podľa round_id (verný port loadDropdownDevices z _old)
  const dropdownDeviceCall = useServerCall<unknown, { rows: IDeviceRow[] }>({
    key: 'deviceDropdownByRound',
    mode: 'control',
    request: { method: 'POST', url: API.deviceCodelist() },
    queryOptions: { refetchOnWindowFocus: false },
    onSuccess: (res) => {
      DeviceDialogStore.setDropdownDevices(res.data?.rows ?? []);
    }
  });

  useEffect(() => {
    if (DeviceDialogStore.isOpen) {
      dropdownDeviceCall.run({ data: getFilters([`round_id=${DeviceDialogStore.roundId}`]) });
    }
  }, [DeviceDialogStore.isOpen, DeviceDialogStore.roundId]);

  const getDefaultValues = (): IDeviceDialogForm => ({
    cityId: DeviceDialogStore.prefillCity?.cityId ?? '',
    street: DeviceDialogStore.prefillCity?.street ?? '',
    device: null,
    deviceCode: '',
    devicePower: '',
    deviceCapacity: '',
    deviceCadastralArea: DeviceDialogStore.prefillCity?.catastral ?? '',
    deviceParcelNumber: DeviceDialogStore.prefillCity?.lot_number ?? '',
    deviceCount: 1,
    deviceInstallationPhotos: [],
    deviceConnectionContract: [],
    deviceOwnerConsent: [],
    deviceLeaseContract: [],
    deviceLabelPhotos: []
  });

  const form = useForm<IDeviceDialogForm>({
    resolver: joiResolver(deviceDialogSchema) as any,
    defaultValues: getDefaultValues() as any
  });

  const { control, handleSubmit, watch, setValue, reset } = form;

  // Dialóg je mountnutý natrvalo — defaultValues sa čítajú len pri mounte.
  // Pri každom otvorení (vrátane „Pridať ďalšie zariadenie", kde sa predvyplní
  // cityId/street z prefillCity) musíme formulár resetovať, inak ostanú prázdne
  // a povinné polia ticho zlyhajú.
  useEffect(() => {
    if (DeviceDialogStore.isOpen) {
      reset(getDefaultValues() as any);
      setInstalledDeviceRows([]);
      setInverterRows([]);
      setBatteryStorageRows([]);
      setBatteryTankRows([]);
    }
  }, [DeviceDialogStore.isOpen, DeviceDialogStore.prefillCity]);

  const selectedDevice: IDeviceRow | null = watch('device');
  const deviceCode = selectedDevice?.code_ext ?? '';
  const showCapacity = DEVICES_WITH_CAPACITY.includes(deviceCode);
  const showCatastral = DEVICES_WITH_CATASTRAL.includes(deviceCode);
  const showCount = RequestFormStore.requestType === 'reimbursement';
  // Sekcie menič/akumulátor (ele) vs. akumulačná nádrž (heat) — riadené devide_group_id kola
  const showInverter = showCount && DeviceDialogStore.deviceGroupId === 'ele';
  const showBatteryStorage = showCount && DeviceDialogStore.deviceGroupId === 'ele';
  const showBatteryTank = showCount && DeviceDialogStore.deviceGroupId === 'heat';
  const [installedSelectOpen, setInstalledSelectOpen] = useState(false);
  // Ak sa InstalledDeviceSelectDialog otvorí z tlačidla "Výber meniča napätia", predvyplní
  // a zablokuje pole "Druh zariadenia" na 'VC' (Menič napätia)
  const [installedSelectPresetTypeCode, setInstalledSelectPresetTypeCode] = useState<string | null>(null);
  // Zoznam vybraných nainštalovaných zariadení — riadky pridávané z InstalledDeviceSelectDialog
  const [installedDeviceRows, setInstalledDeviceRows] = useState<IMockInstalledDeviceRow[]>([]);
  // Zoznam vybraných meničov (invertorov) — riadky pridávané z InstalledDeviceSelectDialog, len ak
  // je zvolený Druh zariadenia "Menič napätia" (code_ext 'VC')
  const [inverterRows, setInverterRows] = useState<IMockInverterRow[]>([]);
  // Zoznam vybraných batériových úložísk — riadky pridávané z InstalledDeviceSelectDialog, len ak
  // je zvolený Druh zariadenia "Akumulátor" (code_ext 'BA')
  const [batteryStorageRows, setBatteryStorageRows] = useState<IMockBatteryStorageRow[]>([]);
  // Zoznam vybraných akumulačných nádrží — riadky pridávané z InstalledDeviceSelectDialog, len ak
  // je zvolený Druh zariadenia "Akumulačná nádrž" (code_ext 'TS')
  const [batteryTankRows, setBatteryTankRows] = useState<IMockBatteryTankRow[]>([]);

  const capacityLabel =
    deviceCode === 'SCWA' || deviceCode === 'HPWA'
      ? t('device_dialog.capacity_liter')
      : t('device_dialog.capacity_kwh');

  const onSubmit = handleSubmit((values) => {
    if (showCount) {
      // Reimbursement tok — zdrojom installed_devices_data sú riadky pridané cez kategorizované
      // tabuľky (InstalledDeviceSelectDialog), nie horné pole "Zariadenie"
      const installedEntries: IInstalledDeviceEntry[] = [
        ...installedDeviceRows.map((row) => ({
          _id: uuidv4(),
          energeticDeviceId: row.energeticDeviceId,
          count: row.count,
          attachments: row.attachments,
          deviceTypeName: row.type,
          manufacturer: row.manufacturer,
          modelType: row.model,
          typeCode: row.typeCode,
          specificationId: row.specificationId,
          manufacturerCode: row.manufacturerCode
        })),
        ...inverterRows.map((row) => ({
          _id: uuidv4(),
          energeticDeviceId: row.energeticDeviceId,
          count: row.count,
          attachments: row.attachments,
          deviceTypeName: row.deviceTypeName,
          manufacturer: row.manufacturer,
          modelType: row.model,
          typeCode: row.typeCode,
          specificationId: row.specificationId,
          manufacturerCode: row.manufacturerCode
        })),
        ...batteryStorageRows.map((row) => ({
          _id: uuidv4(),
          energeticDeviceId: row.energeticDeviceId,
          count: row.count,
          attachments: row.attachments,
          deviceTypeName: row.deviceTypeName,
          manufacturer: row.manufacturer,
          modelType: row.model,
          typeCode: row.typeCode,
          specificationId: row.specificationId,
          manufacturerCode: row.manufacturerCode
        })),
        ...batteryTankRows.map((row) => ({
          _id: uuidv4(),
          energeticDeviceId: row.energeticDeviceId,
          count: row.count,
          attachments: row.attachments,
          deviceTypeName: row.deviceTypeName,
          manufacturer: row.manufacturer,
          modelType: row.model,
          typeCode: row.typeCode,
          specificationId: row.specificationId,
          manufacturerCode: row.manufacturerCode
        }))
      ];
      DeviceDialogStore.addInstalledDevices(
        values.cityId,
        values.street,
        installedEntries,
        values.deviceCadastralArea,
        values.deviceParcelNumber
      );
    } else {
      DeviceDialogStore.addDevice(values);
    }
    reset(getDefaultValues() as any);
  });

  // Pridá riadok do príslušnej tabuľky podľa Druhu zariadenia: Menič napätia (VC) →
  // device_dialog_inverter_table, Akumulátor (BA) → device_dialog_battery_storage_table,
  // Akumulačná nádrž (TS) → device_dialog_battery_tank_table, inak → device_dialog_installed_device_table.
  // Používané pri pridávaní nového riadku aj pri editácii existujúceho (viď handleEditRowConfirm) —
  // ak sa pri editácii zmení Druh zariadenia, riadok sa tak správne presunie do inej tabuľky.
  const addSelectionRow = (selection: IInstalledDeviceSelection) => {
    const model = selection.device.model_type ?? selection.device.name_ext ?? '';
    const manufacturer = selection.device.manufacturer ?? '';
    const energeticDeviceId = selection.device._id;
    const attachments = selection.labelPhotos;
    const deviceTypeName = selection.device.device_type_name ?? '';
    const typeCode = selection.deviceTypeCode;
    const specificationId = selection.specificationId;
    const manufacturerCode = selection.manufacturerCode;
    if (selection.deviceTypeCode === 'VC') {
      setInverterRows((prev) => [
        ...prev,
        {
          manufacturer,
          model,
          count: selection.count,
          energeticDeviceId,
          attachments,
          deviceTypeName,
          typeCode,
          specificationId,
          manufacturerCode
        }
      ]);
    } else if (selection.deviceTypeCode === 'BA') {
      setBatteryStorageRows((prev) => [
        ...prev,
        {
          manufacturer,
          model,
          count: selection.count,
          energeticDeviceId,
          attachments,
          deviceTypeName,
          typeCode,
          specificationId,
          manufacturerCode
        }
      ]);
    } else if (selection.deviceTypeCode === 'TS') {
      setBatteryTankRows((prev) => [
        ...prev,
        {
          manufacturer,
          model,
          count: selection.count,
          energeticDeviceId,
          attachments,
          deviceTypeName,
          typeCode,
          specificationId,
          manufacturerCode
        }
      ]);
    } else {
      setInstalledDeviceRows((prev) => [
        ...prev,
        {
          type: selection.device.device_type_name ?? '',
          specification: selection.specificationName,
          manufacturer,
          model,
          count: selection.count,
          energeticDeviceId,
          attachments,
          typeCode,
          specificationId,
          manufacturerCode
        }
      ]);
    }
  };

  // Potvrdenie z InstalledDeviceSelectDialog — formulár DeviceDialog sa neodosiela, len sa pridá riadok
  const handleInstalledDeviceConfirm = (selection: IInstalledDeviceSelection) => {
    addSelectionRow(selection);
    setInstalledSelectOpen(false);
  };

  const handleClose = () => {
    DeviceDialogStore.close();
    reset(getDefaultValues() as any);
  };

  const deviceOptions: IDeviceRow[] = DeviceDialogStore.dropdownDevices;

  // Riadok práve editovaný cez InstalledDeviceSelectDialog — otvorený z "Akcie" stĺpca nižšie
  const [editTarget, setEditTarget] = useState<{ table: TRowTable; index: number } | null>(null);

  const removeRow = (table: TRowTable, index: number) => {
    if (table === 'installed') setInstalledDeviceRows((prev) => prev.filter((_, i) => i !== index));
    else if (table === 'inverter') setInverterRows((prev) => prev.filter((_, i) => i !== index));
    else if (table === 'batteryStorage') setBatteryStorageRows((prev) => prev.filter((_, i) => i !== index));
    else setBatteryTankRows((prev) => prev.filter((_, i) => i !== index));
  };

  // "Akcie" cell (edit/delete) pre mock tabuľky nižšie — parametrizovaný podľa tabuľky, aby vedel,
  // ktorý zoznam upraviť/odstrániť
  const renderRowActions =
    (table: TRowTable) =>
    ({ row }: any) => (
      <Grid container justifyContent='flex-end' spacing={1}>
        <Grid item>
          <XsIconButton
            icon={<i className='fal fa-pen' />}
            tooltip={String(t('device_dialog.installed_table_edit'))}
            onClick={() => setEditTarget({ table, index: row.index })}
          />
        </Grid>
        <Grid item>
          <XsIconButton
            icon={<i className='fal fa-times fa-lg' />}
            tooltip={String(t('device_dialog.installed_table_delete'))}
            onClick={() => removeRow(table, row.index)}
          />
        </Grid>
      </Grid>
    );

  const editRow: IMockInstalledDeviceRow | IMockInverterRow | IMockBatteryStorageRow | IMockBatteryTankRow | null =
    !editTarget
      ? null
      : editTarget.table === 'installed'
        ? (installedDeviceRows[editTarget.index] ?? null)
        : editTarget.table === 'inverter'
          ? (inverterRows[editTarget.index] ?? null)
          : editTarget.table === 'batteryStorage'
            ? (batteryStorageRows[editTarget.index] ?? null)
            : (batteryTankRows[editTarget.index] ?? null);

  // Potvrdenie editácie cez InstalledDeviceSelectDialog — pôvodný riadok sa odstráni a nahradí
  // novou selekciou cez addSelectionRow (rovnaká logika ako pri pridávaní), nech sa riadok správne
  // presunie do inej tabuľky, ak sa pri editácii zmenil Druh zariadenia
  const handleEditRowConfirm = (selection: IInstalledDeviceSelection) => {
    if (editTarget) removeRow(editTarget.table, editTarget.index);
    addSelectionRow(selection);
    setEditTarget(null);
  };

  // Tabuľka vybraných nainštalovaných zariadení — plnená z InstalledDeviceSelectDialog
  const installedDeviceColumns: TColumnExt<IMockInstalledDeviceRow>[] = [
    { Header: 'device_dialog.installed_table_type', accessor: 'type' },
    { Header: 'device_dialog.installed_table_specification', accessor: 'specification' },
    { Header: 'device_dialog.installed_table_manufacturer', accessor: 'manufacturer' },
    { Header: 'device_dialog.installed_table_model', accessor: 'model' },
    { Header: 'device_dialog.installed_table_count', accessor: 'count', width: 80, align: 'right' },
    {
      Header: 'device_dialog.installed_table_actions',
      accessor: () => null,
      id: 'actions',
      width: 110,
      align: 'right',
      disableSortBy: true,
      Cell: renderRowActions('installed')
    }
  ];
  const installedDeviceTableData = { rows: installedDeviceRows, totalRows: installedDeviceRows.length };

  // Tabuľka meničov (invertorov) — plnená z InstalledDeviceSelectDialog pri Druhu zariadenia "Menič napätia" (VC)
  const inverterColumns: TColumnExt<IMockInverterRow>[] = [
    { Header: 'device_dialog.installed_table_manufacturer', accessor: 'manufacturer' },
    { Header: 'device_dialog.installed_table_model', accessor: 'model' },
    { Header: 'device_dialog.installed_table_count', accessor: 'count', width: 80, align: 'right' },
    {
      Header: 'device_dialog.installed_table_actions',
      accessor: () => null,
      id: 'actions',
      width: 90,
      align: 'right',
      disableSortBy: true,
      Cell: renderRowActions('inverter')
    }
  ];
  const inverterTableData = { rows: inverterRows, totalRows: inverterRows.length };

  // Tabuľka batériových úložísk — mock dáta, zatiaľ nenapojené na reálny stav
  const batteryStorageColumns: TColumnExt<IMockBatteryStorageRow>[] = [
    { Header: 'device_dialog.installed_table_manufacturer', accessor: 'manufacturer' },
    { Header: 'device_dialog.installed_table_model', accessor: 'model' },
    { Header: 'device_dialog.installed_table_count', accessor: 'count', width: 80, align: 'right' },
    {
      Header: 'device_dialog.installed_table_actions',
      accessor: () => null,
      id: 'actions',
      width: 90,
      align: 'right',
      disableSortBy: true,
      Cell: renderRowActions('batteryStorage')
    }
  ];
  const batteryStorageTableData = { rows: batteryStorageRows, totalRows: batteryStorageRows.length };

  // Tabuľka akumulačných nádrží (heat) — mock dáta, zatiaľ nenapojené na reálny stav
  const batteryTankColumns: TColumnExt<IMockBatteryTankRow>[] = [
    { Header: 'device_dialog.installed_table_manufacturer', accessor: 'manufacturer' },
    { Header: 'device_dialog.installed_table_model', accessor: 'model' },
    { Header: 'device_dialog.installed_table_count', accessor: 'count', width: 80, align: 'right' },
    {
      Header: 'device_dialog.installed_table_actions',
      accessor: () => null,
      id: 'actions',
      width: 90,
      align: 'right',
      disableSortBy: true,
      Cell: renderRowActions('batteryTank')
    }
  ];
  const batteryTankTableData = { rows: batteryTankRows, totalRows: batteryTankRows.length };

  return (
    <XsBaseDialog
      open={DeviceDialogStore.isOpen}
      onClose={handleClose}
      title={
        showCount
          ? t('device_dialog.title_installed')
          : DeviceDialogStore.onlyDevice
            ? t('device_dialog.title_device')
            : t('device_dialog.title_place')
      }
      maxWidth='md'
      fullWidth
    >
      <DialogContent>
        <FormProvider {...form}>
          <form onSubmit={onSubmit} noValidate>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* City + street — hidden entirely when pridávame ďalšie zariadenie k existujúcemu miestu
              v rezervačnom toku (onlyDevice a nie reimbursement); readonly zobrazenie v onlyDevice
              režime pri "Zariadenie už mám nainštalované" (napr. "Doplniť inštalované zariadenia"
              pre potvrdenú rezerváciu), inak plne editovateľné (nové miesto). */}
              {(!DeviceDialogStore.onlyDevice || showCount) && (
                <>
                  <Grid item xs={6}>
                    {DeviceDialogStore.onlyDevice ? (
                      <XsTextField
                        value={DeviceDialogStore.prefillCity?.city ?? ''}
                        name='cityId_ro'
                        disabled
                        label={t('device_dialog.city')}
                      />
                    ) : (
                      <Controller
                        name='cityId'
                        control={control}
                        render={({ field }) => (
                          <XsAutocompleteServer
                            {...field}
                            label={t('device_dialog.city')}
                            required
                            minLengthForSearch={1}
                            request={{
                              uri: API.cityCodelist(),
                              payload: {
                                load: (v: string) =>
                                  getFilters(
                                    DeviceDialogStore.regionTypeId
                                      ? [`code=${v}`, `region_type_id=${DeviceDialogStore.regionTypeId}`]
                                      : [`code=${v}`]
                                  ),
                                search: (s: string) =>
                                  getFilters(
                                    DeviceDialogStore.regionTypeId
                                      ? [`search_column=${s}`, `region_type_id=${DeviceDialogStore.regionTypeId}`]
                                      : [`search_column=${s}`]
                                  )
                              }
                            }}
                            identFn={(o: any) => o._id}
                            labelFn={(o: any) => (o ? (o.city_county ?? o.name_ext ?? '') : '')}
                            onApply={(o: any) => {
                              if (!o) return;
                              DeviceDialogStore.setPendingCityName(o.city_county ?? o.name_ext ?? '');
                            }}
                          />
                        )}
                      />
                    )}
                  </Grid>

                  <Grid item xs={6}>
                    {DeviceDialogStore.onlyDevice ? (
                      <XsTextField
                        value={DeviceDialogStore.prefillCity?.street ?? ''}
                        name='street_ro'
                        disabled
                        label={t('device_dialog.street')}
                      />
                    ) : (
                      <Controller
                        name='street'
                        control={control}
                        render={({ field }) => <XsTextField {...field} label={t('device_dialog.street')} required />}
                      />
                    )}
                  </Grid>

                  {/* Katastrálne územie + parcela — povinné pri "Zariadenie už mám nainštalované" (reimbursement),
                  editovateľné aj v onlyDevice režime (na rozdiel od obce/ulice) */}
                  {showCount && (
                    <>
                      <Grid item xs={6}>
                        <Controller
                          name='deviceCadastralArea'
                          control={control}
                          render={({ field }) => (
                            <XsTextField {...field} label={t('device_dialog.cadastral')} required />
                          )}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <Controller
                          name='deviceParcelNumber'
                          control={control}
                          render={({ field }) => <XsTextField {...field} label={t('device_dialog.parcel')} required />}
                        />
                      </Grid>
                    </>
                  )}
                </>
              )}

              {/* Sekcia "Zariadenie" (výber zo GD.CL.Device + výkon/kapacita/katastrálne údaje) —
              len pri rezervačnom toku; pri "Zariadenie už mám nainštalované" nahrádza kaskádový
              výber cez InstalledDeviceSelectDialog nižšie, táto sekcia sa vôbec nezobrazuje */}
              {!showCount && (
                <>
                  {!DeviceDialogStore.onlyDevice && (
                    <Grid item xs={12}>
                      <Typography variant='body1' sx={{ fontWeight: 600 }}>
                        {t('device_dialog.device_group')}
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12} md={4}>
                    <Controller
                      name='device'
                      control={control}
                      render={({ field }) => (
                        <XsSearchSelect<IDeviceRow>
                          {...field}
                          label={t('device_dialog.device')}
                          required
                          options={deviceOptions}
                          getOptionLabel={(o) => o.name_ext ?? ''}
                          getOptionValue={(o) => o._id}
                          postAction={(o: IDeviceRow | null) => {
                            const code = o?.code_ext ?? '';
                            // Synchronizuj plochý kód — riadi podmienené required v Joi schéme
                            setValue('deviceCode', code, { shouldValidate: true });
                            if (!o) {
                              setValue('deviceCapacity', '');
                              setValue('deviceCadastralArea', '');
                              setValue('deviceParcelNumber', '');
                              return;
                            }
                            if (!DEVICES_WITH_CAPACITY.includes(code)) {
                              setValue('deviceCapacity', '');
                            }
                            if (!DEVICES_WITH_CATASTRAL.includes(code)) {
                              setValue('deviceCadastralArea', '');
                              setValue('deviceParcelNumber', '');
                            }
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Power */}
                  <Grid item xs={12} md={3}>
                    <Controller
                      name='devicePower'
                      control={control}
                      render={({ field }) => (
                        <XsNumberField
                          {...field}
                          label={t('device_dialog.power')}
                          required
                          inputProps={{ min: 0.001, step: 0.001 }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Capacity — conditional */}
                  {showCapacity && (
                    <Grid item xs={12} md={3}>
                      <Controller
                        name='deviceCapacity'
                        control={control}
                        render={({ field }) => (
                          <XsNumberField
                            {...field}
                            label={capacityLabel}
                            required
                            inputProps={{ min: 0.001, step: 0.001 }}
                          />
                        )}
                      />
                    </Grid>
                  )}

                  {/* Cadastral area + parcel — conditional */}
                  {showCatastral && (
                    <>
                      <Grid item xs={12} md={4}>
                        <Controller
                          name='deviceCadastralArea'
                          control={control}
                          render={({ field }) => (
                            <XsTextField {...field} label={t('device_dialog.cadastral')} required />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name='deviceParcelNumber'
                          control={control}
                          render={({ field }) => <XsTextField {...field} label={t('device_dialog.parcel')} required />}
                        />
                      </Grid>
                    </>
                  )}
                </>
              )}

              {/* Prílohy k nainštalovanému zariadeniu — len pri reimbursement toku, po dve vedľa seba */}
              {showCount && (
                <>
                  <Grid container item spacing={2} mt={1} mb={3}>
                    <Grid item xs={12}>
                      <XsButton
                        fullWidth={false}
                        label='device_dialog.select_installed_device'
                        variant='contained'
                        // color='success'
                        onClick={() => {
                          setInstalledSelectPresetTypeCode(null);
                          setInstalledSelectOpen(true);
                        }}
                      />
                    </Grid>

                    {/* Zoznam vybraných nainštalovaných zariadení — mock dáta, zatiaľ nenapojené na reálny stav */}
                    <Grid item xs={12}>
                      <XsTableView<IMockInstalledDeviceRow>
                        name='device_dialog_installed_device_table'
                        control='client'
                        columns={installedDeviceColumns}
                        data={installedDeviceTableData}
                        pagination={false}
                        initState={{}}
                      />
                    </Grid>

                    {/* Menič napätia — len pri kole so skupinou zariadení "ele" */}
                    {showInverter && (
                      <>
                        <Grid item xs={12}>
                          <XsButton
                            fullWidth={false}
                            label='device_dialog.select_inverter'
                            variant='contained'
                            // color='success'
                            onClick={() => {
                              setInstalledSelectPresetTypeCode('VC');
                              setInstalledSelectOpen(true);
                            }}
                          />
                        </Grid>

                        {/* Zoznam vybraných meničov (invertorov) — mock dáta, zatiaľ nenapojené na reálny stav */}
                        <Grid item xs={12}>
                          <XsTableView<IMockInverterRow>
                            name='device_dialog_inverter_table'
                            control='client'
                            columns={inverterColumns}
                            data={inverterTableData}
                            pagination={false}
                            initState={{}}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Akumulátor — len pri kole so skupinou zariadení "ele" */}
                    {showBatteryStorage && (
                      <>
                        <Grid item xs={12}>
                          <XsButton
                            fullWidth={false}
                            label='device_dialog.select_battery_storage'
                            variant='contained'
                            // color='success'
                            onClick={() => {
                              setInstalledSelectPresetTypeCode('BA');
                              setInstalledSelectOpen(true);
                            }}
                          />
                        </Grid>

                        {/* Zoznam vybraných batériových úložísk — mock dáta, zatiaľ nenapojené na reálny stav */}
                        <Grid item xs={12}>
                          <XsTableView<IMockBatteryStorageRow>
                            name='device_dialog_battery_storage_table'
                            control='client'
                            columns={batteryStorageColumns}
                            data={batteryStorageTableData}
                            pagination={false}
                            initState={{}}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Akumulačná nádrž — len pri kole so skupinou zariadení "heat" */}
                    {showBatteryTank && (
                      <>
                        <Grid item xs={12}>
                          <XsButton
                            fullWidth={false}
                            label='device_dialog.select_battery_tank'
                            variant='contained'
                            // color='success'
                            onClick={() => {
                              setInstalledSelectPresetTypeCode('TS');
                              setInstalledSelectOpen(true);
                            }}
                          />
                        </Grid>

                        {/* Zoznam vybraných akumulačných nádrží — mock dáta, zatiaľ nenapojené na reálny stav */}
                        <Grid item xs={12}>
                          <XsTableView<IMockBatteryTankRow>
                            name='device_dialog_battery_tank_table'
                            control='client'
                            columns={batteryTankColumns}
                            data={batteryTankTableData}
                            pagination={false}
                            initState={{}}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='body1' sx={{ fontWeight: 600, mb: 1 }}>
                      {t('device_dialog.att_installation_photos')}
                    </Typography>
                    <Controller
                      name='deviceInstallationPhotos'
                      control={control}
                      render={({ field }) => (
                        <XsDropzone
                          name='dropzone_deviceInstallationPhotos'
                          maxFiles={10}
                          accept={['.pdf', '.jpg', '.jpeg', '.png']}
                          value={field.value as IAttachment[]}
                          onChange={(files) => field.onChange(files as IAttachment[])}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='body1' sx={{ fontWeight: 600, mb: 1 }}>
                      {t('device_dialog.att_connection_contract')}
                    </Typography>
                    <Controller
                      name='deviceConnectionContract'
                      control={control}
                      render={({ field }) => (
                        <XsDropzone
                          name='dropzone_deviceConnectionContract'
                          maxFiles={10}
                          accept={['.pdf', '.xls', '.xlsx', '.odf', '.ods', '.odt']}
                          value={field.value as IAttachment[]}
                          onChange={(files) => field.onChange(files as IAttachment[])}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='body1' sx={{ fontWeight: 600, mb: 1 }}>
                      {t('device_dialog.att_owner_consent')}
                    </Typography>
                    <Controller
                      name='deviceOwnerConsent'
                      control={control}
                      render={({ field }) => (
                        <XsDropzone
                          name='dropzone_deviceOwnerConsent'
                          maxFiles={10}
                          accept={['.pdf', '.xls', '.xlsx', '.odf', '.ods', '.odt']}
                          value={field.value as IAttachment[]}
                          onChange={(files) => field.onChange(files as IAttachment[])}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='body1' sx={{ fontWeight: 600, mb: 1 }}>
                      {t('device_dialog.att_lease_contract')}
                    </Typography>
                    <Controller
                      name='deviceLeaseContract'
                      control={control}
                      render={({ field }) => (
                        <XsDropzone
                          name='dropzone_deviceLeaseContract'
                          maxFiles={10}
                          accept={['.pdf', '.xls', '.xlsx', '.odf', '.ods', '.odt']}
                          value={field.value as IAttachment[]}
                          onChange={(files) => field.onChange(files as IAttachment[])}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </form>
        </FormProvider>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <XsButton
          fullWidth={false}
          label='device_dialog.save'
          variant='contained'
          color='success'
          icon={<i className='fal fa-check' />}
          onClick={() => onSubmit()}
        />
      </DialogActions>

      <InstalledDeviceSelectDialog
        open={installedSelectOpen}
        onClose={() => setInstalledSelectOpen(false)}
        onConfirm={handleInstalledDeviceConfirm}
        presetTypeCode={installedSelectPresetTypeCode}
      />

      {/* Editácia existujúceho riadku (tlačidlo "pen" v Akciách) — otvorí modál "Vybrať zariadenie"
      predvyplnený Druh/Špecifikácia/Výrobca/Model/Počet/Fotky (editovateľné); po potvrdení sa
      pôvodný riadok nahradí novou selekciou (viď handleEditRowConfirm) */}
      <InstalledDeviceSelectDialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        onConfirm={handleEditRowConfirm}
        editInitial={
          editRow
            ? {
                typeCode: editRow.typeCode,
                specificationId: editRow.specificationId,
                manufacturerCode: editRow.manufacturerCode,
                energeticDeviceId: editRow.energeticDeviceId,
                modelName: editRow.model,
                count: editRow.count,
                labelPhotos: editRow.attachments
              }
            : null
        }
      />
    </XsBaseDialog>
  );
}

export default observer(DeviceDialog);
